import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { styled } from 'styletron-react';
import {
  DocumentIdentifier,
  waitForDataProtection
} from 'frameworks_drivers/localization/Localization';
import { TechemTheme } from '@techem/techem-theme';
import CenteredLoadingIndicator from 'presentation/components/CenteredLoadingIndicator';
import { Col, Row } from 'react-bootstrap';
import { paths } from 'frameworks_drivers/routes';
import ExternalLink from 'presentation/components/ExternalLink';

const loadingView = (
  <div className="d-flex w-100 h-100 m-auto top-0 start-0" style={{ transitionDuration: '350ms' }}>
    <Row className="mx-auto my-5">
      <Col xs={12} className="d-flex my-3">
        <span className="m-auto">
          <Trans i18nKey="DataProtectionLoadingInfoText" />
        </span>
      </Col>
      <Col xs={12} style={{ minHeight: '300px' }}>
        <CenteredLoadingIndicator />
      </Col>
    </Row>
  </div>
);
const openNewTab = (url: string) => {
  window.open(url, '_blank');
};

const LinkElement = styled('span', {
  cursor: 'pointer',
  color: TechemTheme.colors.primary,
  ':hover': {
    textDecoration: 'underline'
  }
});

const DataProtectionContent: React.FC = () => {
  const { t } = useTranslation(DocumentIdentifier.DataProtection);
  const [contentLoaded, setContentLoaded] = React.useState(false);

  // Loop through all links in translation file and create inline clickable link elements for them.
  // Respecting the order from the translation file, we can then leave the correct link indexing to the translation file instead of adding logic here.
  const linkObjects = Array.from({ length: 27 }, (_, i) => i).map((index) => {
    return (
      <LinkElement
        key={index}
        onClick={() => {
          openNewTab(t(`content.links.${index}.linkUrl`));
        }}
        dangerouslySetInnerHTML={{
          __html: t(`content.links.${index}.linkText`)
        }}
      ></LinkElement>
    );
  });

  const cookieConsentDesc = t(t(`content.links.${4}.linkText`));

  const Paragraph = (props: any) => {
    return (
      <p
        className="copy"
        style={{
          hyphens: 'auto',
          textAlign: props.centered ? 'center' : 'justify'
        }}
      >
        <Trans
          ns={DocumentIdentifier.DataProtection}
          i18nKey={props.i18nKey}
          tOptions={{ skipPostProcessing: true }}
        >
          {linkObjects}
        </Trans>
      </p>
    );
  };

  const contentView = (
    <>
      <main>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_0.title" />
          </h4>
          <Paragraph i18nKey="content.section_0.paragraph_1" />
          <Paragraph i18nKey="content.section_0.paragraph_2" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_1.title" />
          </h4>
          <Paragraph i18nKey="content.section_1.paragraph_1" />
          <Paragraph i18nKey="content.section_1.paragraph_2" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_2.title" />
          </h4>
          <Paragraph i18nKey="content.section_2.paragraph_1" />
          <Paragraph i18nKey="content.section_2.paragraph_2" />
          <Paragraph i18nKey="content.section_2.paragraph_3" />
          <Paragraph i18nKey="content.section_2.paragraph_4" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_3.title" />
          </h4>
          <Paragraph i18nKey="content.section_3.paragraph_1" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_4.title" />
          </h4>
          <Paragraph i18nKey="content.section_4.paragraph_1" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_5.title" />
          </h4>
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_6.title" />
          </h4>
          <Paragraph i18nKey="content.section_6.paragraph_1" />
          <Paragraph i18nKey="content.section_6.paragraph_2" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_7.title" />
          </h4>
          <Paragraph i18nKey="content.section_7.paragraph_1" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_8.title" />
          </h4>
          <Paragraph i18nKey="content.section_8.paragraph_1" />
          <Paragraph i18nKey="content.section_8.paragraph_2" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_9.title" />
          </h4>
          <Paragraph i18nKey="content.section_9.paragraph_1" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_10.title" />
          </h4>
          <Paragraph i18nKey="content.section_10.paragraph_1" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_11.title" />
          </h4>
          <Paragraph i18nKey="content.section_11.paragraph_1" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_12.title" />
          </h4>
          <Paragraph i18nKey="content.section_12.paragraph_1" />
          <Paragraph i18nKey="content.section_12.paragraph_2" />
          <Paragraph i18nKey="content.section_12.paragraph_3" />
          <Paragraph i18nKey="content.section_12.paragraph_4" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_13.title" />
          </h4>
          <Paragraph i18nKey="content.section_13.paragraph_1" />
          <p
            className="copy"
            style={{
              hyphens: 'auto',
              textAlign: 'justify'
            }}
          >
            <LinkElement>
              <ExternalLink
                href={`#${paths.DataProtection}${paths.CookieConsent}`}
                name={cookieConsentDesc}
              ></ExternalLink>
            </LinkElement>
          </p>
          {/* <Paragraph i18nKey="content.section_13.paragraph_2" /> */}
          <Paragraph i18nKey="content.section_13.paragraph_3" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_14.title" />
          </h4>
          <Paragraph i18nKey="content.section_14.paragraph_1" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_15.title" />
          </h4>
          <Paragraph i18nKey="content.section_15.paragraph_1" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_16.title" />
          </h4>
          <Paragraph i18nKey="content.section_16.paragraph_1" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_17.title" />
          </h4>
          <Paragraph i18nKey="content.section_17.paragraph_1" />
          <Paragraph i18nKey="content.section_17.paragraph_2" />
          <Paragraph i18nKey="content.section_17.paragraph_3" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_18.title" />
          </h4>
          <Paragraph i18nKey="content.section_18.paragraph_1" />
          <Paragraph i18nKey="content.section_18.paragraph_2" />
          <Paragraph i18nKey="content.section_18.paragraph_3" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_19.title" />
          </h4>
          <Paragraph i18nKey="content.section_19.paragraph_1" />
          <Paragraph i18nKey="content.section_19.paragraph_2" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_20.title" />
          </h4>
          <Paragraph i18nKey="content.section_20.paragraph_1" />
          <Paragraph i18nKey="content.section_20.paragraph_2" />
          <Paragraph i18nKey="content.section_20.paragraph_3" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_21.title" />
          </h4>
          <Paragraph i18nKey="content.section_21.paragraph_1" />
          <Paragraph i18nKey="content.section_21.paragraph_2" />
          <Paragraph i18nKey="content.section_21.paragraph_3" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_22.title" />
          </h4>
          <Paragraph i18nKey="content.section_22.paragraph_1" />
          <Paragraph i18nKey="content.section_22.paragraph_2" />
        </section>
        <section>
          <h4>
            <Trans
              ns={DocumentIdentifier.DataProtection}
              i18nKey="content.section_23.title"
              tOptions={{ skipPostProcessing: true }}
            />
          </h4>
          <Paragraph i18nKey="content.section_23.paragraph_1" />
          <Paragraph i18nKey="content.section_23.paragraph_2" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_24.title" />
          </h4>
          <Paragraph i18nKey="content.section_24.paragraph_1" />
          <Paragraph i18nKey="content.section_24.paragraph_2" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_25.title" />
          </h4>
          <Paragraph i18nKey="content.section_25.paragraph_1" />
          <Paragraph i18nKey="content.section_25.paragraph_2" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_26.title" />
          </h4>
          <Paragraph i18nKey="content.section_26.paragraph_1" />
          <Paragraph i18nKey="content.section_26.paragraph_2" />
          <Paragraph i18nKey="content.section_26.paragraph_3" />
          <Paragraph i18nKey="content.section_26.paragraph_4" />
          <Paragraph i18nKey="content.section_26.paragraph_5" />
          <Paragraph i18nKey="content.section_26.paragraph_6" />
          <Paragraph i18nKey="content.section_26.paragraph_7" />
          <Paragraph i18nKey="content.section_26.paragraph_8" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_27.title" />
          </h4>
          <Paragraph i18nKey="content.section_27.paragraph_1" />
          <Paragraph i18nKey="content.section_27.paragraph_2" />
          <Paragraph i18nKey="content.section_27.paragraph_3" />
          <Paragraph i18nKey="content.section_27.paragraph_4" />
          <Paragraph i18nKey="content.section_27.paragraph_5" />
          <Paragraph i18nKey="content.section_27.paragraph_6" />
          <Paragraph i18nKey="content.section_27.paragraph_7" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_28.title" />
          </h4>
          <Paragraph i18nKey="content.section_28.paragraph_1" />
          <Paragraph i18nKey="content.section_28.paragraph_2" />
          <Paragraph i18nKey="content.section_28.paragraph_3" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_29.title" />
          </h4>
          <Paragraph i18nKey="content.section_29.paragraph_1" />
          <Paragraph i18nKey="content.section_29.paragraph_2" />
          <Paragraph i18nKey="content.section_29.paragraph_3" />
          <Paragraph i18nKey="content.section_29.paragraph_4" />
          <Paragraph i18nKey="content.section_29.paragraph_5" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_30.title" />
          </h4>
          <Paragraph i18nKey="content.section_30.paragraph_1" />
          <Paragraph i18nKey="content.section_30.paragraph_2" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_31.title" />
          </h4>
          <Paragraph i18nKey="content.section_31.paragraph_1" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_32.title" />
          </h4>
          <Paragraph i18nKey="content.section_32.paragraph_1" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_33.title" />
          </h4>
          <Paragraph i18nKey="content.section_33.paragraph_1" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_34.title" />
          </h4>
          <Paragraph i18nKey="content.section_34.paragraph_1" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.section_35.title" />
          </h4>
          <Paragraph i18nKey="content.section_35.paragraph_1" />
        </section>
      </main>
      <footer className="d-flex flex-column justify-content-center py-3 copy">
        <Paragraph i18nKey="content.footer.website" />
        <div>
          <Trans ns={DocumentIdentifier.DataProtection} i18nKey="content.footer.status" />
        </div>
        <Paragraph i18nKey="content.footer.end" />
      </footer>
    </>
  );

  React.useEffect(() => {
    if (!contentLoaded) {
      const fetchData = async () => {
        waitForDataProtection.then((response: any) => {
          setContentLoaded(true);
        });
      };
      fetchData();
    }
  });

  return !contentLoaded ? loadingView : contentView;
};

export default DataProtectionContent;
